import { Field, Textarea } from '../../../libs/gs-component-form';
import { Content } from '../../../libs/gs-component-right-rail';
import { compose } from '@xo-union/react-css-modules';
import { FormBlockColumn } from '@xo-union/tk-component-form-grid';
import { Field as FormikField } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { Maybe, PersonItemsFragment, Wws_PersonItem } from '../../../generated/graphql';
import { SharedFormProps } from '../../../helpers/useManageItem';
import { Footer } from '../../FormComponents';
import { DeleteOrDiscard } from '../DeleteOrDiscard';
import { ImageField } from '../ImageField';
import { PhotoValues } from '../StoryItem/Form';
import styles from './index.scss';
import { useErrorFocus } from '../../../hooks/useErrorFocus';
import { useAiTextExperimentContext } from '../../../context/AiTextExperimentContext';

export const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  role: Yup.string().required(),
  description: Yup.string(),
});

export interface InitialValues {
  name?: string | null;
  role?: string | null;
  id?: number | null;
  description?: string | null;
  photo: PhotoValues;
}

interface Props extends SharedFormProps {
  item?: Wws_PersonItem;
  pageId: number;

  // Formik props
  errors?: {
    photo?: string;
    name?: string;
    role?: string;
    description?: string;
  };
  isSubmitting?: boolean;
}

export const initialValues = (item?: Maybe<PersonItemsFragment>): InitialValues => ({
  name: item?.name || '',
  role: item?.role || '',
  description: item?.description || '',
  photo: {
    mediaUrl: item?.photo?.mediaUrl || null,
    mediaApiId: item?.photo?.mediaApiId || null,
    width: item?.photo?.width || null,
    height: item?.photo?.height || null,
    cropX: item?.photo?.cropX || null,
    cropY: item?.photo?.cropY || null,
    rotation: item?.photo?.rotation || null,
  },
});

const personItemImageOptions = {
  minWidth: 10,
  maxWidth: 10,
};

const FOCUSABLE_FIELD_NAMES = [
  /*
   * This is a list of field names that can/should be focused upon if/when the form is submitted and the field had an error.
   * They are case-sensitive and must match the field names exactly.
   * Note the photo field is NOT included because any errors that occur there are announced by the screen reader
   * immediately after uploading with an alert (that functionality is build into the `ImageField` component).
   */
  'name',
  'role',
  'description',
];

const Form: React.FC<Props> = ({
  submitForm,
  loading,
  updating,
  discardOrDelete,
  pageId,
  isNew,
  close,
  errors,
  isSubmitting,
}) => {
  // For accessibility, we should focus on the first field with errors (if any), after an attempt at submission occurs.
  useErrorFocus({
    errors,
    isSubmitting,
    focusableFieldNames: FOCUSABLE_FIELD_NAMES,
  });

  const { showAiButton } = useAiTextExperimentContext();

  return (
    <>
      <Content className={styles.content} loading={loading}>
        <FormBlockColumn>
          <div className={styles.imageBox}>
            <FormikField
              component={ImageField}
              name="photo.mediaUrl"
              pageId={pageId}
              parentItemType="PersonItem"
              imageFieldName="photo"
              imageTestId="person-item-upload"
              imageOptions={personItemImageOptions}
            />
          </div>
        </FormBlockColumn>
        <FormBlockColumn>
          <FormikField name="name" label="Name" component={Field} data-testid="name" required={true} autoFocus />
        </FormBlockColumn>
        <FormBlockColumn>
          <FormikField name="role" label="Role" component={Field} data-testid="role" required={true} />
        </FormBlockColumn>
        <FormBlockColumn>
          <FormikField
            data-testid="description"
            classes={compose({ container: styles.fieldContainer })}
            name="description"
            placeholder="Add a short note about this person to introduce them to your guests. (optional)"
            label={false}
            component={Textarea}
            showAiButton={showAiButton}
          />
        </FormBlockColumn>
        <DeleteOrDiscard onClick={discardOrDelete} isNew={isNew}>
          Delete person
        </DeleteOrDiscard>
      </Content>
      <Footer primaryOnClick={submitForm} showSpinner={updating} useSaveAndPreview={true} secondaryOnClick={close} />
    </>
  );
};

export default Form;
